import { DatesProvider } from "../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../i18n/useDateFormatter";
import { Link, createFileRoute } from "@tanstack/react-router";
import api from "../../../../../utils/api";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import Loading from "../../../../../components/core/blocks/Loading";
import { useDates } from "../../../../../dates/DatesContext";
import { useFetch } from "../../../../../hooks/api";
import { useMemo, useState } from "react";
import { Container, Row, Col, Nav, Badge, Table, Card } from "react-bootstrap";
import getMetricSpec from "../../../../../modules/merchandising/utils/metrics";
import Metric from "../../../../../components/core/blocks/SummaryMetric";
import ReactTable from "react-table-6";
import ReactTablePagination from "../../../../../components/custom/growth/ProductsCVTablePagination";
import { makeColumn, col } from "../../../../../components/custom/merchandise/columns";
import useColumnSelector from "../../../../../hooks/useColumnSelector";
import MultiMetricChart from "../../../../../components/custom/merchandise/MultiMetricChart";
import useSelectedMetrics from "../../../../../components/custom/merchandise/hooks/useSelectedMetrics";
import { INVENTORY_STATES } from "../../../../../components/core/blocks/AdPlan/constants";
import moment from "moment";
import checkApplication from "../../../../../utils/checkApplication";
import {
  ADVERTISING,
  CONTENT,
  DEALS,
  DYNAMIC_PRICING,
  SALE_STATE_OPTIONS,
} from "../../../../../components/custom/merchandise/constants";
import ListingQuality from "../../../../../modules/product_quality/components/ListingQuality";
import { getBadgeVariant } from "../../../../../modules/product_quality/utils";
import ConversionLegend from "../../../../../modules/product_quality/components/ConversionLegend";
import { useModuleEnabled } from "../../../../../redux/selectors/modules";
import useCategories from "../../../../../hooks/useCategories";
import { ProductBreadcrumbs } from "../../../../../modules/perf_reports/product_report/ProductBreadcrumbPicker";
import PriceDetails from "../../../../../modules/merchandising/components/PriceDetails";
import { successToast, errorToast } from "../../../../../utils/toasts";
import MetricsCard from "../../../../../components/custom/merchandise/MetricsCard";
import { toTitleCase } from "../../../../../utils/formatText";
import PriceHistorySection from "../../../../../modules/pricingv2/cards/PriceHistory";
import CompetitorsSection from "../../../../../modules/pricingv2/cards/Competitors";
import ConfirmationSection from "../../../../../modules/pricingv2/cards/Confirmation";
import { DemandChartsSection } from "../../../../../modules/pricingv2/cards/DemandTrend";
import { PricingProductUnavailable } from "../../../../../modules/pricingv2/components/PricingProductUnavailable";
import { formatCurrency, formatNumber, formatPercent } from "../../../../../utils/formatNumber";
import PricingPlanCard from "../../../../../modules/pricingv2/PricingPlanCard";
import ExternalProductLink from "../../../../../components/custom/merchandise/ExternalProductLink";
import SalesMapUSA from "../../../../../components/custom/maps/usa";
import BuyBoxOffers from "../../../../../modules/pricingv2/cards/BuyBoxOffers";
import IncomingInventory from "../../../../../components/custom/IncomingInventory";
import ProductInventory from "../../../../../components/custom/ProductInventory";
import { useAppSelector } from "../../../../../redux/hooks";
import ProductShopperInsights from "../../../../../components/custom/merchandise/ProductShopperInsights";
import { ProductSummaryHeader } from "../../../../../modules/merchandising/components/ProductSummaryHeader";
import { invalidateProductQueries } from "../../../../../modules/perf_reports/api/product/invalidateProductQueries";
import TrellisNavLink from "../../../../../components/core/basic/NavLink";
import { PRICING_STATUS_LABELS } from "../../../../../modules/pricingv2/utils";
import CouponReport from "../../../../../modules/merchandising/components/CouponReport";
import { FaCheckCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { trellisPalette } from "../../../../../components/custom/analytics/palettes";
import ChatContextSetter from "../../../../../components/chat/ChatContextSetter";
import SearchQueryPerformanceProductSection from "../../../../../modules/sqpr/SearchQueryPerformanceProductSection";
import useMerchandisePreferences from "../../../../../hooks/useMerchandisePreferences";
import { HoverInfo } from "../../../../../components/core/basic/Tooltip";
import { css } from "../../../../../../styled-system/css";
import { VideoGeneration } from "../../../../../components/custom/video-generation/VideoGeneration";
import useQueryParams from "../../../../../hooks/useQueryParams";
import { AdvertisingSection } from "../../../../../components/custom/merchandise/ProductAdvertisingSection";
import { useTranslation } from "react-i18next";

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;
  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/merchandising/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PRODUCT:
      to = `/user/merchandising/product/${resourceId}`;
      break;
    default:
      to = `/user/dashboard`;
  }
  return to;
};

const ProductDetails = ({ marketPlace, channel, product, start, end, totalUnits }) => {
  const { t } = useTranslation(["merchandising"]);
  const { data: pricingOrg } = useFetch(
    ["pricing/settings", marketPlace, channel],
    "/pricing/settings",
    {},
    {
      default: [],
      select: (d) => d.data?.results[0],
    },
  );

  const hasInventoryModule = pricingOrg?.use_incoming_inventory;

  const [tab, setTab] = useState("general");

  const {
    preferences: { cogs: cogsPreference },
  } = useMerchandisePreferences();
  const landedCostCogsPreference = cogsPreference === "landed_cost";

  const getVariations = () => {
    if (!product?.variations) {
      return <span>{t(($) => $.merchandising["n/a"])}</span>;
    }

    const keys = Object.keys(product?.variations);
    if (keys.length === 0) {
      return <span>{t(($) => $.merchandising["n/a"])}</span>;
    }

    const variations = [];
    for (let i = 0; i < keys.length; i++) {
      const variation = (
        <span className="pe-3" key={keys[i]}>
          {toTitleCase(keys[i])}: {product?.variations[keys[i]]}
        </span>
      );
      variations.push(variation);
    }

    return variations;
  };

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="general" className="text-start">
            {t(($) => $.merchandising["Details"])}
          </TrellisNavLink>

          <TrellisNavLink eventKey="historical-inventory" className="text-start" visible={channel !== "walmart"}>
            {t(($) => $.merchandising["Historical Inventory"])}
          </TrellisNavLink>

          <TrellisNavLink eventKey="cost-fees" className="text-start" visible={channel === "amazon"}>
            {t(($) => $.merchandising["Cost & Fees"])}
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="incoming-inventory"
            className="text-start"
            visible={hasInventoryModule && channel === "amazon"}
          >
            {t(($) => $.merchandising["Incoming Inventory"])}
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="map"
            className="text-start"
            visible={marketPlace.marketPlace === "USA" && channel === "amazon"}
          >
            {t(($) => $.merchandising["Sales By State"])}
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={10}>
        <div className="pt-2">
          {tab === "general" && (
            <Row>
              <Col xs={8}>
                <Table borderless hover>
                  <tbody>
                    <tr>
                      <th>{t(($) => $.merchandising["Title"])}</th>
                      <td>{product?.product_title}</td>
                    </tr>
                    <tr>
                      <th>
                        {channel === "amazon" ?
                          t(($) => $.merchandising["ASIN"])
                        : t(($) => $.merchandising["Channel ID"])}
                      </th>
                      <td>
                        {product?.asin} <ExternalProductLink asin={product?.asin} />
                      </td>
                      ``
                    </tr>
                    <tr>
                      <th>{t(($) => $.merchandising["SKU"])}</th>
                      <td>{product?.sku}</td>
                    </tr>
                    <tr>
                      <th style={{ whiteSpace: "nowrap" }}>{t(($) => $.merchandising["Inventory Qty"])}</th>
                      <td>{product?.product_quantity || product?.product_quantity_fba}</td>
                    </tr>
                    {channel === "amazon" && (
                      <tr>
                        <th>{t(($) => $.merchandising["BSR"])}</th>
                        <td>{product?.bsr_rank}</td>
                      </tr>
                    )}
                    <tr>
                      <th>{t(($) => $.merchandising["Variations"])}</th>
                      <td>{getVariations()}</td>
                    </tr>
                    {product?.parent_asin && product?.parent_asin !== "n/a" && (
                      <tr>
                        <th>{t(($) => $.merchandising["Parent"])}</th>
                        <td>
                          <Link
                            to="/user/merchandising/parent/$parentAsin"
                            params={{ parentAsin: product?.parent_asin }}
                          >
                            {product?.parent_asin}
                          </Link>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
          {tab === "historical-inventory" && (
            <Row>
              <Col xs={12}>
                <ProductInventory
                  productIds={[product.id]}
                  start={start}
                  end={end}
                  productPrice={product.product_price}
                  marketPlace={marketPlace.marketPlace}
                  totalUnits={totalUnits}
                />
              </Col>
            </Row>
          )}
          {tab === "cost-fees" && (
            <Row>
              <Col xs={6}>
                <Table borderless hover>
                  <tbody>
                    <tr>
                      <th>{t(($) => $.merchandising["Fulfillment Channel"])}</th>
                      <td>{product?.fulfillment_channel === "AFN" ? "FBA" : "FBM"}</td>
                    </tr>
                    <tr>
                      <th>{t(($) => $.merchandising["Landed Cost"])}</th>
                      <td>
                        {formatCurrency(product?.landed_cost, {
                          marketPlace: marketPlace,
                        })}
                      </td>
                    </tr>
                    {product?.fulfillment_channel !== "AFN" && (
                      <tr>
                        <th>{t(($) => $.merchandising["Shipping Cost"])}</th>
                        <td>
                          {formatCurrency(product?.shipping_cost, {
                            marketPlace: marketPlace,
                          })}
                        </td>
                      </tr>
                    )}
                    {product?.fulfillment_channel === "AFN" && (
                      <tr>
                        <th>{t(($) => $.merchandising["Amazon Fulfillment Fees"])}</th>
                        <td className={css({ display: "flex", alignItems: "center", gap: "small" })}>
                          {formatCurrency(landedCostCogsPreference ? 0 : product?.fulfillment_fee, { marketPlace })}
                          {landedCostCogsPreference && (
                            <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                              {t(($) => $.merchandising["Amazon fulfillment fees are included in landed cost"])}
                            </HoverInfo>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr style={{ borderBottom: "1px solid #ccc" }}>
                      <th>{t(($) => $.merchandising["Amazon Referral Fees"])}</th>
                      <td className={css({ display: "flex", alignItems: "center", gap: "small" })}>
                        {formatCurrency(landedCostCogsPreference ? 0 : product?.channel_fees * product?.product_price, {
                          marketPlace,
                        })}
                        {landedCostCogsPreference ?
                          <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                            {t(($) => $.merchandising["Amazon referral fees are included in landed cost"])}
                          </HoverInfo>
                        : <span className="ps-3">
                            <small>({formatPercent(product?.channel_fees)})</small>
                          </span>
                        }
                      </td>
                    </tr>
                    <tr>
                      <th>{t(($) => $.merchandising["Product Price"])}</th>
                      <td>
                        {formatCurrency(product?.product_price, {
                          marketPlace: marketPlace,
                        })}
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      <th>{t(($) => $.merchandising["Fees + COGS"])}</th>
                      <td>
                        {landedCostCogsPreference ?
                          formatCurrency(product?.landed_cost, {
                            marketPlace: marketPlace,
                          })
                        : formatCurrency(
                            product?.channel_fees * product?.product_price +
                              product?.fulfillment_fee +
                              product?.shipping_cost +
                              product?.landed_cost,
                            {
                              marketPlace: marketPlace,
                            },
                          )
                        }
                      </td>
                    </tr>
                    <tr>
                      <th>{t(($) => $.merchandising["Net Profit"])}</th>
                      <td>
                        {landedCostCogsPreference ?
                          formatCurrency(product?.product_price - product?.landed_cost, {
                            marketPlace: marketPlace,
                          })
                        : formatCurrency(
                            product?.product_price -
                              (product?.channel_fees * product?.product_price +
                                product?.fulfillment_fee +
                                product?.shipping_cost +
                                product?.landed_cost),
                            {
                              marketPlace: marketPlace,
                            },
                          )
                        }
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <div className="fs-5 pt-2 text-center">
                  <Link to="/user/pl/report" search={{ product_id: String(product.id) }} target="_blank">
                    {t(($) => $.merchandising["View Detailed Cost & Fees"])}
                  </Link>
                </div>
              </Col>
            </Row>
          )}

          {tab === "map" && (
            <Row>
              <Col xs={12}>
                <SalesMapUSA
                  start_date={start.format("YYYY-MM-DD")}
                  end_date={end.format("YYYY-MM-DD")}
                  marketPlace={marketPlace}
                  productId={product?.id}
                />
              </Col>
            </Row>
          )}

          {tab === "incoming-inventory" && (
            <Row>
              <Col xs={12}>
                <IncomingInventory product={product} />
              </Col>
            </Row>
          )}
        </div>
      </Col>
    </Row>
  );
};

const ContentSection = ({ productId }) => {
  const { t } = useTranslation(["merchandising"]);
  const { loading, data } = useFetch(
    ["productQuality", productId],
    `/merchandise/quality/${productId}`,
    {},
    {
      select: (data) => data?.data,
    },
  );
  const metrics = useMemo(
    () => [
      getMetricSpec("seo_score"),
      getMetricSpec("buyability_score"),
      getMetricSpec("conversion_rate"),
      getMetricSpec("ctr"),
    ],
    [],
  );
  const terms = useMemo(() => {
    const seoKeywords = data?.seo_keywords?.keywords;
    const seoSearchTerms = data?.seo_keywords?.search_terms;
    if (seoKeywords && seoSearchTerms) {
      [seoKeywords, seoSearchTerms].forEach((data) => {
        for (let i = 0; i < data.length; i++) {
          const kw = data[i]["keyword_text"];
          const kwData = data[i];
          kwData.cr = 0;
          kwData.ctr = 0;
          kwData.acos = 0;

          if (kw.length <= 1) {
            continue;
          }

          if (kwData.clicks > 0) {
            kwData.cr = kwData.conversions / kwData.clicks;
          }
          if (kwData.impressions > 0) {
            kwData.ctr = kwData.clicks / kwData.impressions;
          }
          if (kwData.sales > 0) {
            kwData.acos = kwData.cost / kwData.sales;
          }

          if (!kwData.cr) {
            kwData.cr = 0;
          }
        }
      });

      seoKeywords.sort(function (a, b) {
        return b.cr - a.cr;
      });

      seoSearchTerms.sort(function (a, b) {
        return b.cr - a.cr;
      });
      return {
        seoKeywords,
        seoSearchTerms,
      };
    }
    return {};
  }, [data]);

  if (loading) {
    return <Loading height={"300px"} fullPage={false} />;
  }
  return (
    <>
      <Row>
        <Col xs={3} className="pt-5 ps-4">
          <Row>
            <Col xs={12}>{data?.content_details && <ListingQuality contentDetails={data?.content_details} />}</Col>
          </Row>
        </Col>
        <Col xs={5} className="pt-5">
          <Row>
            <Col xs={12}>
              <div className="fs-4 pb-2" style={{ fontWeight: "bold" }}>
                {t(($) => $.merchandising["Top Converting Keywords"])}{" "}
                <HoverInfo iconCss={{ color: "grey", display: "inline" }}>
                  <>
                    {t(($) => $.merchandising["The top converting keywords for this product."])}
                    <br />
                    <ConversionLegend xs={6} />
                  </>
                </HoverInfo>
              </div>
              <div
                style={{
                  maxHeight: "600px",
                  overflow: "hidden",
                  overflowY: "scroll",
                  paddingTop: "2px",
                  paddingBottom: "10px",
                }}
              >
                {terms?.seoKeywords &&
                  terms?.seoKeywords?.slice(0, 50)?.map((k) => (
                    <Badge key={k.keyword_text} bg={getBadgeVariant(k)} className="mx-1 my-1 fs-5 badge-keyword">
                      {k.keyword_text}
                    </Badge>
                  ))}
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }}>
                <Metric metric={m} data={data} />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="text-center">
          <Link to="/user/content/seo" search={{ id: String(productId) }}>
            {t(($) => $.merchandising["View Product Content"])}
          </Link>
        </Col>
      </Row>
    </>
  );
};

const CouponPerfIndicator = ({ couponMetric, previousMetric }) => {
  const diffPercent = couponMetric / previousMetric;
  if (diffPercent >= 0.95 && diffPercent <= 1.05) {
    return (
      <span style={{ color: "#aaa" }}>
        <FaCircleMinus />
      </span>
    );
  } else if (diffPercent > 1.05) {
    return (
      <span style={{ color: trellisPalette[12] }}>
        <FaCheckCircle />
      </span>
    );
  }

  return (
    <span style={{ color: "#aaa" }}>
      <FaCircleMinus />
    </span>
  );
};

const DealsSection = ({ productId, marketPlace, categoryId, asin, couponStats, isCouponStatsLoading }) => {
  const { t } = useTranslation(["merchandising"]);
  const [tab, setTab] = useState("coupons");
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["promo_sales"]);
  const { date, start, end, preStart, preEnd, group } = useDates();
  const shortDate = useDateFormatter("short");

  const showCouponPerformance = false;

  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start: preStart.format("YYYY-MM-DD"),
    pre_end: preEnd.format("YYYY-MM-DD"),
    product_id: productId,
    asin: asin,
  };
  const { data: productDealStats, isLoading: isProductDealStatsLoading } = useFetch(
    ["productDealStats", productId, marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/promos_report/",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const { data: couponPerformance, isLoading: isCouponPerformanceLoading } = useFetch(
    ["couponPerformance", marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/coupon_performance",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("promo_sales"),
      getMetricSpec("promo_units"),
      getMetricSpec("rods"),
      getMetricSpec("promo_cost"),
    ],
    [],
  );

  const columnSpecs = useMemo(() => {
    return [
      col(
        t(($) => $.merchandising["Promotion"]),
        "promotion",
        "promotion_report_product.promotion_report.promotion_name",
      ),
      col(
        t(($) => $.merchandising["Status"]),
        "text",
        "status",
      ),
      col(
        t(($) => $.merchandising["Type"]),
        "text",
        "promotionType",
      ),
      col(
        t(($) => $.merchandising["Start"]),
        "date",
        "promotion_report_product.promotion_report.start_datetime",
      ),
      col(
        t(($) => $.merchandising["Deal Price"]),
        "currency",
        "deal_price",
      ),
      col(
        t(($) => $.merchandising["End"]),
        "date",
        "promotion_report_product.promotion_report.end_datetime",
      ),
      col(
        t(($) => $.merchandising["Promo Sales"]),
        "currency",
        "product_revenue",
        {
          compareKey: "sales_est",
          compareType: "est.",
        },
      ),
      col(
        t(($) => $.merchandising["Promo Units"]),
        "number",
        "product_units_sold",
        {
          compareKey: "units_est",
          compareType: "est.",
        },
      ),
      col(
        t(($) => $.merchandising["Total Discounts"]),
        "currency",
        "cost",
      ),
      col(
        t(($) => $.merchandising["Glance Views"]),
        "number",
        "product_glance_views",
      ),
      col(
        t(($) => $.merchandising["Conversion Rate"]),
        "percent",
        "conversion",
      ),
    ];
  }, [t]);

  const promos = useMemo(() => {
    const PROMO_TYPE_LABELS = {
      BEST_DEAL: t(($) => $.merchandising["Best Deal"]),
      LIGHTNING_DEAL: t(($) => $.merchandising["Lightning Deal"]),
    };
    return (
      productDealStats?.promos?.map((p) => {
        const promotionType =
          PROMO_TYPE_LABELS[p?.promotion_report_product?.promotion_report?.promotion_type] ??
          t(($) => $.merchandising["Unknown"]);
        let status = p?.promotion_report_product?.promotion_report?.status;
        const start = p?.promotion_report_product?.promotion_report?.start_datetime;
        const end = p?.promotion_report_product?.promotion_report?.end_datetime;
        if (status === "APPROVED") {
          if (moment(start).isBefore(moment()) && moment(end).isAfter(moment())) {
            status = t(($) => $.merchandising["In Progress"]);
          } else if (moment(end).isBefore(moment())) {
            status = t(($) => $.merchandising["Completed"]);
          } else {
            status = t(($) => $.merchandising["Approved"]);
          }
        } else {
          status = status === "CANCELED" ? t(($) => $.merchandising["Canceled"]) : status?.toLowerCase;
        }
        return {
          ...p,
          status,
          promotionType,
        };
      }) ?? []
    );
  }, [productDealStats, t]);

  const createColumn = makeColumn(productDealStats, marketPlace, false, date, false, categoryId, shortDate);
  const [columns] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs],
  );
  const columnOrder = columns.map((col) => col.id);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isProductDealStatsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="coupons" className="text-start">
            {t(($) => $.merchandising["Coupons"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="deals" className="text-start">
            {t(($) => $.merchandising["Deals"])}
          </TrellisNavLink>
        </Nav>
      </Col>
      {tab === "deals" && (
        <Col xs={10}>
          <Row>
            <Col xs={8}>
              aaa
              {productDealStats?.dates && (
                <MultiMetricChart
                  metrics={metrics}
                  selectedMetrics={selectedMetrics}
                  data={productDealStats.dates}
                  group={group}
                  handleLegendChange={handleLegendChange}
                />
              )}
            </Col>
            <Col xs={4} className="pt-5">
              <Row>
                {metrics.map((m) => (
                  <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }} onClick={() => toggleMetric(m.id)}>
                    <Metric
                      metric={m}
                      data={productDealStats?.total}
                      showTarget
                      selected={selectedMetrics.includes(m.id)}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              {promos?.length > 0 && (
                <ReactTable
                  pageSizeOptions={[5, 10, 20, 25, 50]}
                  showPaginationBottom={false}
                  showPaginationTop
                  PaginationComponent={ReactTablePagination}
                  columns={columns}
                  data={promos ?? []}
                  defaultPageSize={promos?.length ?? 0}
                  className="-highlight"
                  filterable={false}
                  state={columnOrder}
                  getPaginationProps={() => ({
                    hideSearch: true,
                    hideFilters: true,
                    table: "plan-products",
                    style: { marginBottom: "2rem" },
                    carouselLayout: false,
                    disableSearchFocus: true,
                  })}
                />
              )}
            </Col>
          </Row>
        </Col>
      )}
      {tab === "coupons" && (
        <Col xs={10} className="p-4">
          {!isCouponStatsLoading && !isCouponPerformanceLoading && (
            <>
              <Row>
                <Col xs={12}>
                  <div
                    style={{
                      padding: "2rem",
                      border: "1px solid rgb(238, 238, 238)",
                      borderRadius: "6px",
                      backgroundColor: "rgb(248, 248, 248)",
                      marginBottom: "15px",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    className="fs-5"
                  >
                    <div className="fw-bold">
                      {t(
                        ($) =>
                          $.merchandising[
                            "The following table shows the performance of your coupons by discount amount."
                          ],
                      )}
                    </div>
                    <div className="ps-2 pt-2 fs-5">
                      <span style={{ color: trellisPalette[12] }}>
                        <FaCheckCircle />
                      </span>{" "}
                      {t(
                        ($) =>
                          $.merchandising[
                            "Specifies a positive change in performance compared to a period of time where a coupon was not active."
                          ],
                      )}
                    </div>
                    <div className="ps-2 pt-2 fs-5">
                      <span style={{ color: "#aaa" }}>
                        <FaCircleMinus />
                      </span>{" "}
                      {t(
                        ($) =>
                          $.merchandising[
                            "Specifies a neutral performance compared to a period of time where a coupon was not active."
                          ],
                      )}
                    </div>
                  </div>
                  <Table>
                    <thead>
                      <tr>
                        <th>{t(($) => $.merchandising["Discount %"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["Sales"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["Units"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["GCD"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["CTR"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["CPC"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["CR"])}</th>
                        <th className="text-start">{t(($) => $.merchandising["BSR"])}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {couponPerformance?.map((c) => (
                        <tr key={c.discount_amount}>
                          <td className="text-center">
                            {c.discount_amount === null ?
                              t(($) => $.merchandising["All Discounts"])
                            : c.discount_type === "PERCENT_OFF_LIST_PRICE" ?
                              `${formatPercent(c.discount_amount / 100, {
                                decimalPlaces: 0,
                              })}`
                            : `${formatCurrency(c.discount_amount, {
                                marketPlace: marketPlace.marketPlace,
                              })}`
                            }
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator
                                  couponMetric={c.coupon_total_sales}
                                  previousMetric={c.previous_total_sales}
                                />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>
                                    {formatCurrency(c.coupon_total_sales, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                  <div>
                                    {formatCurrency(c.previous_total_sales, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </td>

                          <td>
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator
                                  couponMetric={c.coupon_total_units_ordered}
                                  previousMetric={c.previous_total_units_ordered}
                                />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatNumber(c.coupon_total_units_ordered, { decimalPlaces: 0 })}</div>
                                  <div>{formatNumber(c.previous_total_units_ordered, { decimalPlaces: 0 })}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_gcd} previousMetric={c.previous_gcd} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>
                                    {formatCurrency(c.coupon_gcd, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                  <div>
                                    {formatCurrency(c.previous_gcd, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_ctr} previousMetric={c.previous_ctr} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatPercent(c.coupon_ctr)}</div>
                                  <div>{formatPercent(c.previous_ctr)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_cpc} previousMetric={c.previous_cpc} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>
                                    {formatCurrency(c.coupon_cpc, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                  <div>
                                    {formatCurrency(c.previous_cpc, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_cr} previousMetric={c.previous_cr} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatPercent(c.coupon_cr)}</div>
                                  <div>{formatPercent(c.previous_cr)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-left">
                            <CouponPerfIndicator
                              couponMetric={c.coupon_bsr_rank}
                              previousMetric={c.previous_bsr_rank}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <CouponReport data={couponStats} marketPlace={marketPlace} asinReport />
                </Col>
              </Row>
            </>
          )}
          {(isCouponStatsLoading || isCouponPerformanceLoading) && <Loading height={"300px"} fullPage={false} />}
        </Col>
      )}
    </Row>
  );
};

const PRICING_PRODUCT_TABS = new Set(["price_history", "ad_affect", "confirmation", "pricing_plan"]);

const PricingSection = ({ productId, marketPlace, product, isOverviewDataLoading, pricingInfo, user }) => {
  const { t } = useTranslation(["merchandising"]);
  const [tab, setTab] = useState("price_history");

  const {
    data: pricingProduct,
    isLoading: isPricingProductDataLoading,
    error: pricingProductError,
  } = useFetch(
    ["product_pricing", productId],
    `/pricing/products/${productId}/`,
    { get_primary: "false" },
    {
      select: (d) => d?.data,
      placeholderData: keepPreviousData,
    },
  );

  const pricingProductUnavailable = !isPricingProductDataLoading && !pricingProduct;

  const { data: historyData, isLoading: isHistoryDataLoading } = useFetch(
    ["price_history", productId],
    `/pricing/price_history`,
    {
      product_ids: productId,
    },
    {
      select: (d) => d?.data,
      keepPreviousData: false,
    },
  );

  const showPricingPlanLink = () => {
    return Boolean(pricingProduct?.plan_id && (user.is_staff || pricingProduct.visible));
  };

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="price_history" className="text-start">
            {t(($) => $.merchandising["Price History"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="ad_affect" className="text-start">
            {t(($) => $.merchandising["Advertising Effectiveness"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="competitors" className="text-start">
            {t(($) => $.merchandising["Competitors"])}
          </TrellisNavLink>

          <TrellisNavLink eventKey="confirmation" className="text-start" visible={showPricingPlanLink()}>
            {t(($) => $.merchandising["Price Changes"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="pricing_plan" className="text-start" visible={showPricingPlanLink()}>
            {t(($) => $.merchandising["View Pricing Plan"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="details" className="text-start">
            {t(($) => $.merchandising["Amazon Price Details"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="buy_box" className="text-start">
            {t(($) => $.merchandising["Buy Box Offers"])}
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={10} className="p-3">
        <Row className="pb-3 mb-3" style={{ borderBottom: "1px solid #ccc" }}>
          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                {t(($) => $.merchandising["Pricing"])}
              </h6>
              <Badge
                className={`badge bg-trellis-green`}
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {PRICING_STATUS_LABELS[pricingInfo.status]}
              </Badge>
            </div>
          </Col>
          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                {t(($) => $.merchandising["Enroll Date"])}
              </h6>
              <Badge
                className={`badge bg-trellis-lightgrey text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>
                  {pricingInfo.enrollDate ?
                    moment(pricingInfo.enrollDate).format("MM/DD/YYYY")
                  : t(($) => $.merchandising["N/A"])}
                </span>
              </Badge>
            </div>
          </Col>

          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                {t(($) => $.merchandising["Fixed Price"])}
              </h6>
              <Badge
                className="badge bg-trellis-purple"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {pricingInfo.priceOverrideEnd ?
                  formatCurrency(pricingInfo.priceOverride, {
                    marketPlace: marketPlace.marketPlace,
                  })
                : t(($) => $.merchandising["No"])}
              </Badge>
            </div>
          </Col>

          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                {t(($) => $.merchandising["Pricing Plan"])}
              </h6>

              <Badge
                className={`badge bg-trellis-lightgrey text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                {pricingProduct?.plan_id && (
                  <Link
                    to="/user/pricing/plan/$plan"
                    params={{ plan: String(pricingProduct?.plan_id) }}
                    style={{ color: "#000", textDecoration: "none", fontWeight: "bold" }}
                  >
                    {t(($) => $.merchandising["Yes"])}
                  </Link>
                )}
                {!pricingProduct?.plan_id && <span>{t(($) => $.merchandising["Yes"])}</span>}
              </Badge>
            </div>
          </Col>

          {pricingInfo?.bestPriceDate && (
            <Col>
              <div className="pe-5 text-center">
                <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                  {t(($) => $.merchandising["Competitive Price"])}
                </h6>

                <Badge
                  className={`badge bg-trellis-lightgrey text-end`}
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  {(
                    !isHistoryDataLoading &&
                    historyData[productId] !== undefined &&
                    historyData[productId].external !== null
                  ) ?
                    formatCurrency(historyData[productId].external, {
                      marketPlace: marketPlace.marketPlace,
                    })
                  : `-`}
                </Badge>
              </div>
            </Col>
          )}
        </Row>

        {pricingProductUnavailable && PRICING_PRODUCT_TABS.has(tab) ?
          <PricingProductUnavailable error={pricingProductError} />
        : <>
            {tab === "buy_box" && (
              <Row>
                <Col xs={12}>
                  <BuyBoxOffers asin={product?.asin} />
                </Col>
              </Row>
            )}

            {tab === "details" && (
              <PriceDetails
                productId={productId}
                marketPlace={marketPlace}
                isHistoryDataLoading={isHistoryDataLoading}
                historyData={historyData}
              />
            )}
            {tab === "pricing_plan" && (
              <>
                <Row className="pb-3 text-center">
                  <Col>
                    <Link to="/user/pricing/plan/$plan" params={{ plan: String(pricingProduct?.plan_id) }}>
                      {t(($) => $.merchandising["View Pricing Plan"])}
                    </Link>
                  </Col>
                </Row>
                <PricingPlanCard
                  planId={pricingProduct?.plan_id}
                  hasBorder={false}
                  initialTab="configuration"
                  showTabs={false}
                />
              </>
            )}
            {tab === "demand_charts" && (
              <DemandChartsSection
                pricingProduct={pricingProduct}
                product={product}
                isPricingProductDataLoading={isPricingProductDataLoading}
                pricingProductError={pricingProductError}
              />
            )}
            {tab === "price_history" && (
              <>
                <Card>
                  <DemandChartsSection
                    pricingProduct={pricingProduct}
                    product={product}
                    isPricingProductDataLoading={isPricingProductDataLoading}
                    pricingProductError={pricingProductError}
                  />
                </Card>
                <hr />
                <PriceHistorySection
                  pricingProduct={pricingProduct}
                  pricingProductError={pricingProductError}
                  marketPlace={marketPlace}
                  isPricingProductDataLoading={isPricingProductDataLoading}
                  isLoading={false}
                />
              </>
            )}
            {tab === "ad_affect" && (
              <PriceHistorySection
                pricingProduct={pricingProduct}
                pricingProductError={pricingProductError}
                marketPlace={marketPlace}
                isPricingProductDataLoading={isPricingProductDataLoading}
                isLoading={isOverviewDataLoading}
                advertising={true}
              />
            )}
            {tab === "confirmation" && (
              <ConfirmationSection pricingProduct={pricingProduct} marketPlace={marketPlace} />
            )}
            {tab === "competitors" && <CompetitorsSection productId={productId} marketPlace={marketPlace} />}
          </>
        }
      </Col>
    </Row>
  );
};

const ProductCard = ({ productId, marketPlace, channel, user, data, isOverviewDataLoading }) => {
  const { t } = useTranslation(["merchandising"]);
  const { start, end, group } = useDates();
  const hasAMCModule = useModuleEnabled("AMC");
  const hasDebugModule = useModuleEnabled("DEBUG");
  const hasSQPReportV2ModuleEnabled = useModuleEnabled("SQPR_V2");
  const isSeller = useAppSelector((state) => state.user.organization_type === "seller");

  const { data: product, isLoading: isProductLoading } = useFetch(
    [],
    `/api/gvads/productid/${productId}`,
    {},
    {
      select: (res) => res.data,
    },
  );

  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    product_id: productId,
    asin: product ? product.asin : null,
  };

  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["productDealStats", marketPlace, group, start, end],
    "/merchandise/coupons_reports/",
    filters,
    {
      select: (data) => data?.data,
      enabled: product !== null && product !== undefined,
    },
  );

  const { cellHighlightDates, cellHighlightKey } = useMemo(() => {
    if (!couponStats || !couponStats?.results) return { cellHighlightKey: null, cellHighlightDates: null };
    const dates = [];

    for (const coupon of couponStats.results) {
      const startTime = moment(coupon.start_time);
      const endTime = moment(coupon.end_time);

      let t = startTime;
      while (t.isSameOrBefore(endTime)) {
        dates.push(t.format("YYYY-MM-DD"));
        t = t.add(1, "days");
      }
    }

    return { cellHighlightKey: t(($) => $.merchandising["Units"]), cellHighlightDates: dates };
  }, [couponStats, t]);

  const { pricingInfo } = useMemo(() => {
    const pricingInfo = {
      minPrice: null,
      maxPrice: null,
      enrollDate: null,
      status: null,
      priceOverride: null,
      priceOverrideEnd: null,
      productPrice: null,
      bestPriceDate: null,
      amazonStatus: null,
    };

    if (data) {
      pricingInfo.minPrice = data?.products[0]?.min_price;
      pricingInfo.maxPrice = data?.products[0]?.max_price;
      pricingInfo.enrollDate = data?.products[0]?.enroll_date;
      pricingInfo.status = data?.products[0]?.pricing_plan__status;
      pricingInfo.priceOverride = data?.products[0]?.price_override;
      pricingInfo.priceOverrideEnd = data?.products[0]?.price_override_end;
      pricingInfo.productPrice = data?.products[0]?.product_price;
      pricingInfo.bestPriceDate = data?.products[0]?.best_price_date;
      pricingInfo.amazonStatus = data?.products[0]?.amazon_status;
    }

    return { pricingInfo };
  }, [data]);

  const categoryId = data?.products[0]?.category_id;

  const isLoading = isProductLoading || isOverviewDataLoading;

  const overviewMetrics = useMemo(() => {
    if (channel === "shopify") {
      return [getMetricSpec("sales"), getMetricSpec("units"), getMetricSpec("margin")];
    }

    return [
      getMetricSpec("sales"),
      getMetricSpec("units"),
      getMetricSpec("bsr"),
      getMetricSpec("page_views"),
      getMetricSpec("price"),

      getMetricSpec("margin"),
      getMetricSpec("margin_wo_adspend"),

      getMetricSpec("cost"),
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      getMetricSpec("acos"),
      getMetricSpec("roas"),
      getMetricSpec("total_acos"),
      getMetricSpec("troas"),
      getMetricSpec("roms"),

      getMetricSpec("rating"),

      getMetricSpec("boosted_profit"),
      getMetricSpec("profit"),
      getMetricSpec("profit_wo_adspend"),

      getMetricSpec("profit_per_page_view"),
      getMetricSpec("profit_per_page_view_wo_adspend"),
    ];
  }, [channel]);

  const salesStateArray = SALE_STATE_OPTIONS;
  const inventoryStateArray = [
    INVENTORY_STATES.OVERSTOCK,
    INVENTORY_STATES.LOW_STOCK,
    INVENTORY_STATES.OUT_OF_STOCK,
    INVENTORY_STATES.IN_STOCK,
    INVENTORY_STATES.IN_STOCK_SCARCE,
  ];
  const salesLabel = salesStateArray.find((s) => s.value === product?.sales_state)?.label;
  const inventoryBadgeText = inventoryStateArray.find((s) => s.value === product?.stock_status)?.label;
  const [tab, setTab] = useQueryParams("tab", "details");
  const queryClient = useQueryClient();

  const updateTags = useMutation({
    mutationFn: async (updatedTags) => {
      return await api.patch(`/api/gvads/productid/${productId}`, {
        tags: [...new Set(updatedTags)],
      });
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.merchandising["Product Tags Successfully Updated"]),
      });

      invalidateProductQueries(queryClient);
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.merchandising["Failed to Update Product Tags"]),
        error,
      });
    },
  });

  if (!isLoading && !product) {
    return (
      <Row>
        <Col xs={12}>
          <div className="text-center py-5">
            <h3>{t(($) => $.merchandising["Product not found"])}</h3>
            <p>{t(($) => $.merchandising["The requested product could not be found."])}</p>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {isLoading || isCouponStatsLoading ?
        <Loading height={"300px"} fullPage={false} />
      : <Row>
          <Col xs={12} className="mb-4 fs-standard">
            <MetricsCard
              initialSelectedMetric={["sales", "units", "roms"]}
              titleElement={
                <ProductSummaryHeader
                  product={product}
                  inventoryBadgeText={inventoryBadgeText}
                  salesLabel={salesLabel}
                  pricingInfo={pricingInfo}
                  updateTags={updateTags.mutate}
                />
              }
              metrics={overviewMetrics}
              totals={{
                data: data?.total,
                loading: isOverviewDataLoading,
              }}
              timeline={{
                data: data?.date || [],
                loading: isOverviewDataLoading,
              }}
              group={group}
              showMetricsSelector={channel !== "shopify"}
              id={"productDashboard"}
              showAnnotations={true}
              selectedProduct={productId}
              context={"product"}
              extendMetrics={channel !== "shopify"}
              cellHighlightKey={cellHighlightKey}
              cellHighlightDates={cellHighlightDates}
            >
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    <TrellisNavLink eventKey="details" visible={checkApplication(user, DYNAMIC_PRICING)}>
                      {t(($) => $.merchandising["Product Details"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="pricing" visible={checkApplication(user, DYNAMIC_PRICING)}>
                      {t(($) => $.merchandising["Dynamic Pricing"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="advertising" visible={checkApplication(user, ADVERTISING)}>
                      {t(($) => $.merchandising["Advertising"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="deals" visible={checkApplication(user, DEALS)}>
                      {t(($) => $.merchandising["Promos"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="content" visible={checkApplication(user, CONTENT)}>
                      {t(($) => $.merchandising["Content"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="loyalty" visible={hasAMCModule && isSeller}>
                      {t(($) => $.merchandising["Shopper Insights"])}
                    </TrellisNavLink>
                    <TrellisNavLink
                      eventKey="video-generation"
                      visible={channel === "amazon" && marketPlace.marketPlace === "USA"}
                    >
                      {t(($) => $.merchandising["Video Generation"])}
                    </TrellisNavLink>
                    {(hasDebugModule || hasSQPReportV2ModuleEnabled) && (
                      <TrellisNavLink eventKey="sqpr" visible={channel === "amazon"}>
                        {t(($) => $.merchandising["Search Query Performance"])}
                      </TrellisNavLink>
                    )}
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  {tab === "advertising" && <AdvertisingSection productId={productId} />}
                  {tab === "deals" && (
                    <DealsSection
                      productId={productId}
                      marketPlace={marketPlace}
                      categoryId={categoryId}
                      asin={product?.asin}
                      couponStats={couponStats}
                      isCouponStatsLoading={isCouponStatsLoading}
                    />
                  )}
                  {tab === "pricing" && (
                    <PricingSection
                      productId={productId}
                      marketPlace={marketPlace}
                      isOverviewDataLoading={isOverviewDataLoading}
                      product={product}
                      pricingInfo={pricingInfo}
                      user={user}
                    />
                  )}
                  {tab === "content" && <ContentSection productId={productId} />}
                  {tab === "details" && (
                    <ProductDetails
                      product={product}
                      marketPlace={marketPlace}
                      channel={channel}
                      start={start}
                      end={end}
                      totalUnits={data?.total?.total_units}
                    />
                  )}
                  {tab === "loyalty" && <ProductShopperInsights productId={productId} marketPlace={marketPlace} />}
                  {tab === "video-generation" && <VideoGeneration productId={productId} asin={product?.asin} />}
                  {tab === "sqpr" && (
                    <SearchQueryPerformanceProductSection
                      asin={{ asin: product.asin, product_id: product.id.toString() }}
                    />
                  )}
                </Col>
              </Row>
            </MetricsCard>
          </Col>
        </Row>
      }
    </>
  );
};

const Product = () => {
  const { t } = useTranslation(["merchandising"]);
  const { product: productId } = Route.useParams();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);

  const { start, end, preStart, preEnd, group } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
    product_id: productId,
    channel: channel?.currentChannel ?? "amazon",
  };

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    ["product_report", marketPlace.marketPlace, start, end, preStart, preEnd, productId],
    `/api/data_report/product_report`,
    filters,
    {
      select: (d) => ({
        ...d.data,
        date: d.data.date?.map((row) => ({ ...row, start_date: row.start_date?.slice(0, 10) })),
      }),
      placeholderData: keepPreviousData,
      enabled: !!productId,
    },
  );

  const categoryId = data?.products[0]?.category_id ?? data?.products[0]?.product_category;

  const { currentCategoryDetails: categoryDetails, categories: categoryNavData } = useCategories(
    categoryId,
    "merchandising",
  );

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <ChatContextSetter
        context={`Product Details Page: ${data?.products[0]?.product_title} (${productId}) asin: ${data?.products[0]?.asin} sku: ${data?.products[0]?.sku} Category: ${categoryDetails?.leaf_category_name} (${categoryDetails?.id})`}
      />
      <Row>
        <Col xs={12} className="mb-4">
          <ProductBreadcrumbs
            categoryId={categoryId}
            productId={productId}
            categoryDetails={categoryDetails}
            categoryNavData={categoryNavData}
            dashboardName={t(($) => $.merchandising["Merchandising Dashboard"])}
            dashboardHref={getDashboardTo({ resourceId: null, type: DASHBOARD_TYPES.ROOT })}
            getProductHref={(id) => `/user/merchandising/product/${id}`}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mb-4 px-3">
          <ProductCard
            productId={productId}
            marketPlace={marketPlace}
            user={user}
            channel={channel?.currentChannel ?? "amazon"}
            data={data}
            isOverviewDataLoading={isOverviewDataLoading}
          />
        </Col>
      </Row>
    </Container>
  );
};

const MerchProduct = () => {
  return (
    <DatesProvider>
      <Product />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/merchandising/product/$product")({
  component: MerchProduct,
});
